import { useState, useMemo } from 'react'
import { Search, CheckCircle, AlertTriangle, Clock, ChevronDown, ChevronUp } from 'lucide-react'
import type { BenefitVoucher } from '../types'
import type { ClientStatus } from '@/modules/clients/types'
import { BenefitStatusBadge } from './BenefitStatusBadge'
import { ConfirmDialog } from '@/shared/components/ui/molecules/ConfirmDialog'
import { useConfig } from '@/modules/points/hooks/useConfig'

function getCategoryColor(category: string | null): string {
  switch (category?.toLowerCase()) {
    case 'oro':
      return 'bg-yellow-100 text-yellow-800'
    case 'marmol':
      return 'bg-gray-100 text-gray-800'
    case 'bronce':
      return 'bg-orange-100 text-orange-800'
    default:
      return 'bg-blue-100 text-blue-800'
  }
}

interface VoucherRedeemFormProps {
  voucher: BenefitVoucher | null
  clientStatus: ClientStatus | null
  isSearching: boolean
  searchError: string | null
  onSearch: (code: string) => void
  onClear: () => void
  onRedeem: (code: string, product: string, amount: number) => void
  isRedeeming: boolean
  redeemError: string | null
  redeemSuccess: boolean
  redeemedVoucher: BenefitVoucher | null
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('es-CL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatCLP(amount: number | null): string {
  if (amount === null || amount === undefined) return '-'
  return `$${amount.toLocaleString('es-CL')}`
}

export function VoucherRedeemForm({
  voucher,
  clientStatus,
  isSearching,
  searchError,
  onSearch,
  onClear,
  onRedeem,
  isRedeeming,
  redeemError,
  redeemSuccess,
  redeemedVoucher,
}: VoucherRedeemFormProps) {
  const [code, setCode] = useState('')
  const [showConfirm, setShowConfirm] = useState(false)

  // Product/amount fields for redeem
  const [redeemProduct, setRedeemProduct] = useState('')
  const [redeemAmount, setRedeemAmount] = useState<number | ''>('')

  // Product catalog
  const { config } = useConfig()
  const [isProductsOpen, setIsProductsOpen] = useState(false)
  const [productSearch, setProductSearch] = useState('')

  const filteredProducts = useMemo(() => {
    if (!config?.products) return []
    const sorted = [...config.products].sort((a, b) =>
      String(a.name ?? '').localeCompare(String(b.name ?? '')),
    )
    if (!productSearch.trim()) return sorted
    const query = productSearch.toLowerCase()
    return sorted.filter(
      (p) =>
        String(p.name ?? '').toLowerCase().includes(query) ||
        String(p.sku ?? '').toLowerCase().includes(query),
    )
  }, [config?.products, productSearch])

  const handleSearch = () => {
    if (code.trim()) {
      onSearch(code.trim().toUpperCase())
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const handleNewSearch = () => {
    setCode('')
    setRedeemProduct('')
    setRedeemAmount('')
    setProductSearch('')
    setIsProductsOpen(false)
    onClear()
  }

  const handleSelectProduct = (productName: string, productPoints: number) => {
    setRedeemProduct(productName)
    // Use the point value in CLP as a reference for amount
    // Products in config have `points` — use value_point_to_CLP to convert
    const clpValue = config?.value_point_to_CLP
      ? productPoints * config.value_point_to_CLP
      : productPoints
    setRedeemAmount(clpValue)
  }

  const canRedeem =
    redeemProduct.trim().length > 0 &&
    typeof redeemAmount === 'number' &&
    redeemAmount > 0

  const displayVoucher = redeemedVoucher ?? voucher

  return (
    <div className="max-w-2xl">
      {/* Search input */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold mb-4">Buscar vale</h2>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              onKeyDown={handleKeyDown}
              placeholder="Ingresa el código del vale (ej: VALE-A1B2C3)"
              className="input pl-9 font-mono"
              disabled={isSearching}
            />
          </div>
          <button
            onClick={handleSearch}
            disabled={!code.trim() || isSearching}
            className="btn btn-primary"
          >
            {isSearching ? 'Buscando...' : 'Buscar'}
          </button>
        </div>

        {searchError && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
            <AlertTriangle size={16} />
            {searchError}
          </div>
        )}

        {redeemError && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
            <AlertTriangle size={16} />
            {redeemError}
          </div>
        )}
      </div>

      {/* Success message */}
      {redeemSuccess && (
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
          <CheckCircle size={24} className="text-green-600 flex-shrink-0" />
          <div>
            <p className="font-medium text-green-800">Vale canjeado exitosamente</p>
            <p className="text-sm text-green-700 mt-1">
              El vale {displayVoucher?.voucher_code} ha sido canjeado correctamente.
            </p>
          </div>
        </div>
      )}

      {/* Voucher details */}
      {displayVoucher && (
        <div className="mt-4 card">
          {/* Client info */}
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold mb-3">Datos del cliente</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Nombre</p>
                <p className="font-medium">{displayVoucher.client.name} {displayVoucher.client.last_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">RUT</p>
                <p className="font-medium">{displayVoucher.client.rut}</p>
              </div>
              {clientStatus?.category && (
                <div>
                  <p className="text-sm text-gray-500">Nivel</p>
                  <span className={`inline-block mt-0.5 px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(clientStatus.category)}`}>
                    {clientStatus.category}
                  </span>
                </div>
              )}
              {clientStatus && (
                <div>
                  <p className="text-sm text-gray-500">Puntos disponibles</p>
                  <p className="font-medium">{(clientStatus.available_points ?? 0).toLocaleString('es-CL')}</p>
                </div>
              )}
            </div>
          </div>

          {/* Voucher info */}
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold mb-3">Detalle del vale</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Código</p>
                <p className="font-mono font-medium">{displayVoucher.voucher_code}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Estado</p>
                <BenefitStatusBadge status={displayVoucher.status} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Tipo</p>
                <p className="font-medium capitalize">{displayVoucher.voucher_type.replace(/_/g, ' ')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Descripción</p>
                <p className="font-medium">{displayVoucher.gift_description || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Válido desde</p>
                <p className="font-medium">{formatDate(displayVoucher.valid_from)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Válido hasta</p>
                <p className="font-medium">{formatDate(displayVoucher.valid_until)}</p>
              </div>
            </div>
          </div>

          {/* Product to deliver — only for pending vouchers before redeem */}
          {displayVoucher.status === 'pending' && !redeemSuccess && (
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold mb-3">Producto a entregar</h3>

              {/* Product catalog (collapsible, optional) */}
              {config?.products && config.products.length > 0 && (
                <div className="mb-4 border rounded-lg">
                  <button
                    type="button"
                    onClick={() => setIsProductsOpen(!isProductsOpen)}
                    className="flex items-center justify-between w-full p-3 text-left text-sm"
                  >
                    <span className="font-medium text-gray-700">
                      Seleccionar del catálogo ({config.products.length} productos)
                    </span>
                    {isProductsOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>

                  {isProductsOpen && (
                    <div className="px-3 pb-3">
                      <div className="relative mb-2">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          value={productSearch}
                          onChange={(e) => setProductSearch(e.target.value)}
                          placeholder="Buscar producto por nombre o SKU..."
                          className="input pl-8 text-sm"
                        />
                      </div>

                      <div className="grid grid-cols-1 gap-1.5 max-h-60 overflow-y-auto">
                        {filteredProducts.length === 0 ? (
                          <p className="text-sm text-gray-500 py-3 text-center">
                            No se encontraron productos
                          </p>
                        ) : (
                          filteredProducts.map((product) => (
                            <div
                              key={product.sku}
                              className="flex items-center justify-between p-2.5 border rounded hover:bg-gray-50"
                            >
                              <div>
                                <p className="font-medium text-sm">{product.name}</p>
                                <p className="text-xs text-gray-500">
                                  SKU: {product.sku} - {product.points} pts
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleSelectProduct(product.name, product.points)}
                                className="btn btn-secondary text-xs px-2 py-1"
                              >
                                Seleccionar
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Manual product/amount inputs */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Producto *
                  </label>
                  <input
                    type="text"
                    value={redeemProduct}
                    onChange={(e) => setRedeemProduct(e.target.value)}
                    placeholder="Nombre del producto entregado"
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Monto ($) *
                  </label>
                  <input
                    type="number"
                    value={redeemAmount}
                    onChange={(e) => setRedeemAmount(e.target.value === '' ? '' : Number(e.target.value))}
                    placeholder="Valor en CLP"
                    min={0}
                    className="input"
                  />
                </div>
              </div>
              {!canRedeem && (redeemProduct || redeemAmount) && (
                <p className="text-sm text-amber-600 mt-2">
                  Ambos campos (producto y monto) son obligatorios para canjear.
                </p>
              )}
            </div>
          )}

          {/* Redeemed info (if already redeemed) */}
          {(displayVoucher.status === 'redeemed' && displayVoucher.redeemed_at) && (
            <div className="p-6 border-b bg-green-50">
              <h3 className="text-lg font-semibold mb-3 text-green-800">Información de canje</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-green-600">Fecha de canje</p>
                  <p className="font-medium text-green-800">{formatDate(displayVoucher.redeemed_at)}</p>
                </div>
                {displayVoucher.redeemed_by && (
                  <div>
                    <p className="text-sm text-green-600">Canjeado por</p>
                    <p className="font-medium text-green-800">{displayVoucher.redeemed_by.name}</p>
                  </div>
                )}
                {displayVoucher.redeemed_at_branch && (
                  <div>
                    <p className="text-sm text-green-600">Sucursal</p>
                    <p className="font-medium text-green-800">{displayVoucher.redeemed_at_branch.address}</p>
                  </div>
                )}
                {displayVoucher.redeemed_product && (
                  <div>
                    <p className="text-sm text-green-600">Producto entregado</p>
                    <p className="font-medium text-green-800">{displayVoucher.redeemed_product}</p>
                  </div>
                )}
                {displayVoucher.redeemed_amount !== null && displayVoucher.redeemed_amount !== undefined && (
                  <div>
                    <p className="text-sm text-green-600">Monto</p>
                    <p className="font-medium text-green-800">{formatCLP(displayVoucher.redeemed_amount)}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Expired message */}
          {displayVoucher.status === 'expired' && (
            <div className="p-6 bg-gray-50 flex items-center gap-3">
              <Clock size={20} className="text-gray-400" />
              <p className="text-gray-600">
                Este vale ha expirado el {formatDate(displayVoucher.valid_until)}.
              </p>
            </div>
          )}

          {/* Cancelled message */}
          {displayVoucher.status === 'cancelled' && (
            <div className="p-6 bg-red-50 flex items-center gap-3">
              <AlertTriangle size={20} className="text-red-400" />
              <p className="text-red-600">Este vale ha sido cancelado.</p>
            </div>
          )}

          {/* Actions */}
          <div className="p-6">
            {displayVoucher.status === 'pending' && !redeemSuccess && canRedeem && (
              <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-3">
                El cliente obtiene este producto GRATIS, este monto es solo referencial.
              </p>
            )}
            <div className="flex gap-3">
            {displayVoucher.status === 'pending' && !redeemSuccess && (
              <button
                onClick={() => setShowConfirm(true)}
                disabled={isRedeeming || !canRedeem}
                className="btn btn-primary disabled:opacity-50"
              >
                {isRedeeming ? 'Canjeando...' : 'Confirmar canje'}
              </button>
            )}
            <button onClick={handleNewSearch} className="btn btn-secondary">
              Nueva búsqueda
            </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm redeem dialog */}
      <ConfirmDialog
        isOpen={showConfirm}
        title="Confirmar canje de vale"
        message={
          displayVoucher
            ? `Confirmar canje del vale ${displayVoucher.voucher_code} para ${displayVoucher.client.name} ${displayVoucher.client.last_name}?\n\nRegalo: ${displayVoucher.gift_description || 'N/A'}\nProducto: ${redeemProduct}\nMonto: ${formatCLP(typeof redeemAmount === 'number' ? redeemAmount : 0)}`
            : ''
        }
        confirmLabel="Canjear"
        onConfirm={() => {
          setShowConfirm(false)
          if (displayVoucher && typeof redeemAmount === 'number') {
            onRedeem(displayVoucher.voucher_code, redeemProduct, redeemAmount)
          }
        }}
        onCancel={() => setShowConfirm(false)}
        isLoading={isRedeeming}
      />
    </div>
  )
}
